/* assets/rt-product-card.js — product card augment
   Restructures Broadcast's product cards on the storefront:
   - Paints the colour swatches from data-swatch-image
   - Wraps whatever badges the theme already renders into a stacked container
   - Idempotent: safe to run multiple times (skips already-processed cards) */

(function () {
  const PROCESSED = 'data-rt-card-processed';

  function paintSwatches(card) {
    card.querySelectorAll('.swatch__button[data-swatch-image]').forEach((s) => {
      const url = s.getAttribute('data-swatch-image');
      if (url && !s.style.backgroundImage) {
        s.style.backgroundImage = "url('" + url + "')";
        s.style.backgroundSize = 'cover';
        s.style.backgroundPosition = 'center';
      }
    });
  }

  function augmentCard(card) {
    paintSwatches(card);
    if (card.hasAttribute(PROCESSED)) return;
    card.setAttribute(PROCESSED, '1');

    const existingBadges = Array.from(card.querySelectorAll('.badge-box, .sale-box, .preorder-box'));
    if (existingBadges.length === 0) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'rt-product-badges align--top-left';

    existingBadges.forEach((b) => {
      b.classList.add('badge-box');
      wrapper.appendChild(b);
    });

    const imageHost = card.querySelector('.product-item__image, .product-item__bg, .product-card--featured-image');
    if (imageHost) {
      if (getComputedStyle(imageHost).position === 'static') {
        imageHost.style.position = 'relative';
      }
      imageHost.appendChild(wrapper);
    } else {
      card.appendChild(wrapper);
    }
  }

  function run() {
    document.querySelectorAll('.product-item').forEach(augmentCard);
  }

  function paintAllSwatches() {
    document.querySelectorAll('.product-item').forEach(paintSwatches);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  window.addEventListener('load', paintAllSwatches);
  setTimeout(paintAllSwatches, 600);
  setTimeout(paintAllSwatches, 1500);

  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:section:reorder', run);

  if (window.MutationObserver) {
    const obs = new MutationObserver((mutations) => {
      let needsRun = false;
      let needsPaint = false;
      mutations.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.classList?.contains('product-item') || n.querySelector?.('.product-item')) needsRun = true;
          if (n.matches?.('.swatch__button') || n.querySelector?.('.swatch__button')) needsPaint = true;
        });
      });
      if (needsRun) run();
      if (needsPaint) paintAllSwatches();
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
})();


/* rt-card-swatches — versões da cor no card (snippets/rt-product-card.liquid).
   Uma linha só: conta quantos quadrados cabem na largura, esconde o resto e mostra "+N"
   (link para a PDP) no lugar do último. Hover num quadrado mostra essa cor e, ao sair da
   tira, volta a escolhida. Clicar (ou tocar, no mobile) escolhe a cor: fica a foto e o
   ?variant= dessa cor nos dois links. Custom element: liga-se sozinho nos cards que o
   infinite scroll acrescenta e nas secções recarregadas no Theme Editor. */
class RtCardSwatches extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.rt-card-swatches__item'));
    this.more = this.querySelector('.rt-card-swatches__more');
    this.img = this.closest('.rt-product-card').querySelector('.rt-product-card__media img');
    // a foto com que o card abriu: é para ela que se volta ao sair da tira sem ter clicado
    this.chosen = this.img && { src: this.img.getAttribute('src'), srcset: this.img.srcset, alt: this.img.alt };
    // a foto principal nem sempre é a de uma cor: aí marca-se a 1ª
    if (!this.querySelector('[aria-pressed="true"]')) this.items[0]?.setAttribute('aria-pressed', 'true');
    this.addEventListener('click', this);
    this.addEventListener('mouseover', this);
    this.addEventListener('mouseleave', this);
    this.observer = new ResizeObserver(() => this.fit());
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this);
    this.removeEventListener('mouseover', this);
    this.removeEventListener('mouseleave', this);
    this.observer.disconnect();
  }

  fit() {
    const width = this.clientWidth;
    if (!width) return;
    const style = getComputedStyle(this);
    const size = parseFloat(style.getPropertyValue('--rt-swatch-size')) || 37;
    const gap = parseFloat(style.columnGap) || 0;
    const fits = Math.max(Math.floor((width + gap) / (size + gap)), 1);
    const total = this.items.length;
    let shown = total <= fits ? total : fits - 1;
    // data-max (Theme settings → rt_card_swatch_max): no máximo N cores e o resto em +N
    const max = parseInt(this.dataset.max, 10) || 0;
    if (max && shown > max) shown = max;
    this.items.forEach((item, i) => { item.hidden = i >= shown; });
    this.more.hidden = shown === total;
    this.more.textContent = '+' + (total - shown);
  }

  handleEvent(event) {
    if (event.type === 'mouseleave') {
      this.show(this.chosen);
      return;
    }
    const item = event.target.closest('.rt-card-swatches__item');
    if (!item) return;
    this.show(item.dataset);
    if (event.type !== 'click') return;
    this.chosen = item.dataset;
    // a 2.ª foto do hover é da cor com que o card abriu: escolhida outra, deixa de fazer sentido
    this.closest('.rt-product-card').querySelector('.rt-product-card__hover')?.remove();
    this.closest('.rt-product-card').querySelectorAll('.rt-product-card__media, .rt-product-card__info').forEach((link) => { link.href = item.dataset.url; });
    this.items.forEach((other) => other.setAttribute('aria-pressed', String(other === item)));
  }

  show(photo) {
    if (!photo || !this.img || this.img.getAttribute('src') === photo.src) return;
    this.img.srcset = photo.srcset;
    this.img.src = photo.src;
    this.img.alt = photo.alt;
  }
}
if (!customElements.get('rt-card-swatches')) customElements.define('rt-card-swatches', RtCardSwatches);


/* rt: recommendations track arrows (delegated — content arrives via AJAX).
   Arrows DISAPPEAR at the ends ([disabled] -> opacity 0 in CSS). */
function rtUpdateRelArrows(track) {
  const vp = track.closest('.rt-relgrid__viewport');
  if (!vp) return;
  const prev = vp.querySelector('[data-rt-rel-prev]');
  const next = vp.querySelector('[data-rt-rel-next]');
  if (!prev || !next) return;
  prev.disabled = track.scrollLeft <= 2;
  next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
}
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-rt-rel-prev], [data-rt-rel-next]');
  if (!btn) return;
  const track = btn.closest('.rt-relgrid__viewport')?.querySelector('.rt-relgrid');
  if (!track) return;
  const dir = btn.hasAttribute('data-rt-rel-prev') ? -1 : 1;
  track.scrollBy({ left: dir * track.clientWidth * 0.9, behavior: 'smooth' });
});
/* scroll doesn't bubble — capture phase catches the track's own scroll */
document.addEventListener('scroll', (e) => {
  if (e.target instanceof Element && e.target.classList?.contains('rt-relgrid')) rtUpdateRelArrows(e.target);
}, true);
window.addEventListener('resize', () => document.querySelectorAll('.rt-relgrid').forEach(rtUpdateRelArrows), { passive: true });
if (window.MutationObserver) {
  new MutationObserver((muts) => {
    for (const m of muts) for (const n of m.addedNodes) {
      if (n.nodeType === 1 && (n.matches?.('.rt-relgrid') || n.querySelector?.('.rt-relgrid'))) {
        document.querySelectorAll('.rt-relgrid').forEach(rtUpdateRelArrows);
        return;
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}
